import { Conditional, SkeletonLoader } from '@payaca/ambrosia';
import { useScroll } from '@payaca/hooks';
import { FC, useState } from 'react';

import useGetMaterialCategories from '@/api/queries/materials/useGetMaterialCategories';
import { useGetInfiniteMaterials } from '@/api/queries/materials/useGetMaterials';
import useGetMyAvailableTags from '@/api/queries/me/useGetMyAvailableTags';
import { useGetAllPaginatedSuppliers } from '@/api/queries/suppliers/useGetSuppliers';
import { GetMaterialsInput, MaterialsQuery } from '@/gql/graphql';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import InfiniteList from '@payaca/components/infiniteList/InfiniteList';

import { clstx } from '@payaca/utilities/cvaMerge';
import { singularPlural } from '@payaca/utilities/stringUtilities';
import { parseFulfillments } from '../../pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/fulfillment';
import LiveBranchStockMaterialSupplierBadge from '../../pages/projectPage/components/MaterialsPanel/LiveAvailabilityMaterialSupplierBadge/LiveBranchStockMaterialSupplierBadge';
import MaterialSuppliersBadges from '../../pages/projectPage/components/MaterialSuppliersBadges';
import MaterialCard from '../materialCard/MaterialCard';

type Material = MaterialsQuery['materials']['items'][number];

export interface IProps {
  onAddMaterial?: () => void;
  onMaterialClick?: (material: Material) => void;
  disabledMaterials?: Material['id'][];
  showPriceIncludingTax?: boolean;
  defaultFilters?: GetMaterialsInput;
  projectId?: number;
}

const InfiniteMaterialsList: FC<IProps> = (props) => {
  const {
    onAddMaterial,
    onMaterialClick,
    disabledMaterials = [],
    showPriceIncludingTax = true,
    defaultFilters = {},
    projectId,
  } = props;

  const [scrollRef, setScrollRef] = useState<HTMLDivElement | null>(null);
  const { y: scrollY } = useScroll({ current: scrollRef });

  const [getMaterialsInput, setGetMaterialsInput] =
    useState<GetMaterialsInput>(defaultFilters);

  const compactNumberFormat = usePreferredNumberFormat({
    notation: 'compact',
    compactDisplay: 'short',
  });

  const {
    data: materials,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useGetInfiniteMaterials(getMaterialsInput, { projectId });

  const { suppliers } = useGetAllPaginatedSuppliers({}, 100);
  const { materialCategories } = useGetMaterialCategories();
  const { data: allAvailableTagsOnAccount } = useGetMyAvailableTags();

  const suppliersFilters =
    suppliers?.map((supplier) => {
      const isCurrentlySelected = getMaterialsInput.suppliers?.some(
        (i) => i === supplier.id
      );

      return {
        id: supplier.id,
        label: supplier.name,
        selected: isCurrentlySelected,
        onClick: () => {
          setGetMaterialsInput((state) => ({
            ...state,
            suppliers: isCurrentlySelected
              ? state.suppliers?.filter((i) => i !== supplier.id)
              : [...(state.suppliers || []), supplier.id],
          }));
        },
      };
    }) || [];

  const categoriesFilters =
    materialCategories?.map((category) => {
      const isCurrentlySelected = getMaterialsInput.categories?.some(
        (i) => i === category.id
      );

      return {
        id: category.id,
        label: category.name,
        selected: isCurrentlySelected,
        onClick: () => {
          setGetMaterialsInput((state) => ({
            ...state,
            categories: isCurrentlySelected
              ? state.categories?.filter((i) => i !== category.id)
              : [...(state.categories || []), category.id],
          }));
        },
      };
    }) || [];

  const tagsFilters =
    allAvailableTagsOnAccount?.map((tag) => {
      const isCurrentlySelected = getMaterialsInput.tags?.some(
        (i) => i === tag.id
      );

      return {
        id: tag.id,
        label: (
          <>
            <div
              style={{
                backgroundColor: tag.colour || '#ccc',
              }}
              className="w-3 h-3 rounded-full"
            ></div>{' '}
            {tag.name}
          </>
        ),
        searchKey: tag.name,
        selected: isCurrentlySelected,
        onClick: () => {
          setGetMaterialsInput((state) => ({
            ...state,
            tags: isCurrentlySelected
              ? state.tags?.filter((i) => i !== tag.id)
              : [...(state.tags || []), tag.id],
          }));
        },
      };
    }) || [];

  const totalMaterialsFound = materials?.pages[0]?.materials.totalCount || 0;

  return (
    <InfiniteList ref={setScrollRef}>
      <InfiniteList.Header className={clstx(scrollY > 0 && 'shadow')}>
        <div className="flex items-center gap-2.5">
          <InfiniteList.Search
            className="w-full"
            placeholder="Search materials"
            onChangeTimeout={(value) => {
              setGetMaterialsInput((s) => ({
                ...s,
                searchTerm: value,
              }));
            }}
          />

          <InfiniteList.Filter filters={tagsFilters} name="Tags" />
        </div>

        <InfiniteList.Actionbar>
          <InfiniteList.Filter filters={categoriesFilters} name="Category" />

          <InfiniteList.Filter filters={suppliersFilters} name="Suppliers" />

          <Conditional condition={!!onAddMaterial}>
            <InfiniteList.PrimaryAction
              className="flex-auto"
              onClick={onAddMaterial}
            >
              Create new material
            </InfiniteList.PrimaryAction>
          </Conditional>
        </InfiniteList.Actionbar>
      </InfiniteList.Header>

      <InfiniteList.List
        skeletonItem={<SkeletonLoader.MaterialCard />}
        isLoading={isLoading}
        prefix={
          <Conditional condition={!isLoading && totalMaterialsFound > 0}>
            <p className="supporting-body text-right">
              {singularPlural(
                totalMaterialsFound,
                'Material found',
                'Materials found',
                {
                  lengthFormatted:
                    compactNumberFormat.format(totalMaterialsFound),
                }
              )}
            </p>
          </Conditional>
        }
        emptyStateText="No materials found"
        numOfSkeletonLoaders={12}
        canLoadMore={hasNextPage}
        onLoadMore={() => {
          if (hasNextPage) {
            void fetchNextPage();
          }
        }}
        items={
          materials?.pages.map((m) => m.materials.items.map((i) => i)).flat() ||
          []
        }
        item={(material) => {
          return (
            <MaterialCard
              key={material.id}
              name={material.name}
              tags={material.tags}
              price={
                showPriceIncludingTax
                  ? material.suppliedBy[0]?.price.unitPrice
                  : material.suppliedBy[0]?.price.unitPriceExcTax
              }
              thumbnailUrl={material.image?.thumbnailUrl || ''}
              actionOnClick={() => {
                onMaterialClick?.(material);
              }}
              actionDisabled={disabledMaterials.includes(material.id)}
            >
              <MaterialSuppliersBadges
                suppliers={material.suppliedBy.map((s) => ({
                  id: s.supplier.id,
                  name: s.supplier.name,
                }))}
                fulfillments={parseFulfillments(material.fulfillments)}
                LiveAvailabilityMaterialSupplierBadge={
                  LiveBranchStockMaterialSupplierBadge
                }
              />
            </MaterialCard>
          );
        }}
      />
    </InfiniteList>
  );
};

export default InfiniteMaterialsList;
